import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectionModel } from '@angular/cdk/collections';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatChipSet, MatChipsModule } from '@angular/material/chips';
import { fromEvent } from 'rxjs';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { ButtonSpacerDirective } from '../directives/button.directive';
import { CapitalizePipeModule } from 'app/pipes/format/capitalize.module';
import { AnimatedDelayDirective, AnimatedDirective } from '../directives/animated.directive';

export interface WidgetGroupItem {
    label: string;
    total: number;
}

@UntilDestroy()
@Component({
    selector: 'app-widget-group',
    templateUrl: './widget-group.component.html',
    styleUrls: ['./widget-group.component.scss'],
    standalone: true,
    imports: [
        MatToolbarModule,
        FlexSpacerDirective,
        MatIconModule,
        ButtonSpacerDirective,
        NgIf,
        MatButtonModule,
        CapitalizePipeModule,
        MatChipsModule,
        NgForOf,
        AnimatedDirective,
        AnimatedDelayDirective,
        NgClass,
    ],
})
export class AppWidgetGroupComponent<T> implements OnInit, OnChanges
{
    @Input() data: T[];
    @Input() labelKey = 'label';
    @Input() totalKey = 'total';

    @Output() widgetSelected: EventEmitter<WidgetGroupItem> = new EventEmitter<WidgetGroupItem>();

    @HostBinding('class.two-grid-layout') twoGridLayout = false;

    isLoadingInitialData = true;
    containerWidth = 0;
    widgets: WidgetGroupItem[] = [];
    widgetIndex = 0;
    selectedWidget: SelectionModel<WidgetGroupItem> = new SelectionModel<WidgetGroupItem>(null);

    constructor(private _elementRef: ElementRef) {
    }

    @ViewChild('loadedWidgetList') set content(content: MatChipSet) {
        if (content && !this.isLoadingInitialData) {
            this.updateContainerWidth();
        }
    }

    get lastPage() {
        return this.widgetIndex + (this.twoGridLayout ? 2 : 4) > this.widgets.length - 1;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('data') && this.data) {
            this.widgets = [
                ...this.data.map((item) => {
                    return {
                        label: item[this.labelKey],
                        total: item[this.totalKey],
                    } as WidgetGroupItem;
                })
            ];

            if (!this.selectedWidget.hasValue()) {
                this.selectedWidget.select(this.widgets[0]);
            }

            this.isLoadingInitialData = false;
        }
    }

    ngOnInit() {
        fromEvent(window, 'resize').pipe(untilDestroyed(this)).subscribe(() => this.updateContainerWidth());
    }

    updateContainerWidth() {
        this.containerWidth = this._elementRef.nativeElement.clientWidth;
        this.twoGridLayout = this.containerWidth < 768;
    }

    prevSet() {
        const newIndex = this.widgetIndex - (this.twoGridLayout ? 2 : 4);
        this.widgetIndex = newIndex >= 0 ? newIndex : 0;
    }

    nextSet() {
        const newIndex = this.widgetIndex + (this.twoGridLayout ? 2 : 4);
        this.widgetIndex = newIndex <= this.widgets.length - 1 ? newIndex : this.widgets.length - 1;
    }

    toggleWidget(widget: WidgetGroupItem) {
        if (this.selectedWidget.isSelected(widget)) {
            return;
        }

        this.selectedWidget.select(widget);
        this.widgetSelected.emit(widget);
    }

    isHighlighted(widget: WidgetGroupItem) {
        return  this.selectedWidget.isSelected(widget);
    }
}
